import { useCallback, useEffect, useState } from 'react'
import { Progress } from '../../../globalTypes'
import { useDebounce } from '../../../hooks/useDebounce'

export type UseSelectWithPaginationProps<Option> = {
  progress: Progress
  /**
   * Callback that is called when the list is scrolled to the end.
   * @param search The current search query.
   * @param page The current page number.
   * @param pageSize The page size.
   */
  onUpdateRequested: (search: string, page: number, pageSize: number) => void
  /**
   * Initial array of options
   */
  initialOptions?: Option[]
  /**
   * Initial page
   *
   * @default 1
   */
  initialPage?: number
  /**
   * Here you need to pass new values that you want to add to the end of the list. The reference to the array must be memoized.
   */
  newOptions?: Option[]
  /**
   * The number of options on a page.
   * @default 50
   */
  pageSize?: number
  /**
   * The total number of options that need to be shown.
   */
  total: number
  /**
   * Array of additional dependencies which clears the whole list.
   */
  clearDeps?: any[]
}

export const useSelectWithPaginationState = <Option>({
  initialOptions = [],
  initialPage = 1,
  onUpdateRequested,
  newOptions,
  pageSize = 50,
  total,
  progress,
  clearDeps = [],
}: UseSelectWithPaginationProps<Option>) => {
  const [options, setOptions] = useState<Option[]>(initialOptions)

  const [search, setSearch] = useState('')
  const [page, setPage] = useState(initialPage)
  const debouncedSearch: string = useDebounce(search, 100)

  // Reset options
  useEffect(() => {
    setPage(initialPage)
    onUpdateRequested(debouncedSearch, initialPage, pageSize)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, ...clearDeps])

  // Call onUpdateRequested on page change
  useEffect(() => {
    if (progress !== Progress.WORK && page !== initialPage) {
      onUpdateRequested(debouncedSearch, page, pageSize)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  // Adding new options to the end of the list
  useEffect(() => {
    if (newOptions) {
      setOptions(prevOptions => {
        if (page === initialPage) return newOptions
        return [...prevOptions, ...newOptions]
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newOptions])

  const fetchNextPage = useCallback(() => {
    if (progress !== Progress.WORK) {
      setPage(page => ++page)
    }
  }, [progress])

  return {
    page,
    options,
    setOptions,
    setSearch,
    setPage,
    search: debouncedSearch,
    fetchNextPage:
      page * pageSize <= total && total > pageSize && search === debouncedSearch ? fetchNextPage : undefined,
  }
}
